<script setup>
  import { storeToRefs } from "pinia";
  import { Trash, Plus } from "lucide-vue-next";
  import { Button } from "@/components/ui/button";
  import { Switch } from "@/components/ui/switch";
  import { Checkbox } from "@/components/ui/checkbox";
  import { Select } from "@/components/episto-ui/select";
  import { LabeledField } from "@/components/episto-ui/labeled-field";
  import { useMAudienceStore } from "@stores/mAudience/mAudience";

  const audienceStore = useMAudienceStore();
  const {
    getMAudience,
    getAgeOptions,
    isGenderSegmentationActive,
    ageSegmentOptions,
    getMAudienceAgeBrackets,
    canRemoveAgeBracket,
    canStopAgeSplit,
  } = storeToRefs(audienceStore);
  const { updateMAudienceTGender, applyAgePreset, addAgeBracket, removeAgeBracket, updateAgeBracket, stopAgeSplit } =
    audienceStore;
</script>

<template>
  <div>
    <h4 class="heading-section mt-4 mb-3">Demographics</h4>
    <LabeledField label="Age">
      <div class="flex gap-1 mb-4">
        <Button
          v-for="option in ageSegmentOptions"
          :key="option.value"
          variant="outline"
          size="sm"
          @click="applyAgePreset(option.value)">
          {{ option.label }}
        </Button>
      </div>
      <p class="text-sm text-muted-foreground mb-2">Each age bracket becomes its own ad set.</p>
      <div
        v-for="(bracket, index) in getMAudienceAgeBrackets"
        :key="bracket.token ?? `bracket-${index}`"
        class="grid grid-cols-[1fr_1fr_auto] gap-2 mb-2 items-center">
        <Select
          :model-value="String(bracket.min ?? '')"
          :options="getAgeOptions"
          filterable
          @update:model-value="updateAgeBracket(index, 'min', $event)" />
        <Select
          :model-value="String(bracket.max ?? '')"
          :options="getAgeOptions"
          filterable
          @update:model-value="updateAgeBracket(index, 'max', $event)" />
        <Button
          variant="ghost"
          size="icon-sm"
          :disabled="!canRemoveAgeBracket"
          @click="removeAgeBracket(index)">
          <Trash class="h-4 w-4" />
        </Button>
      </div>
      <div class="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          @click="addAgeBracket">
          <Plus class="h-4 w-4 mr-1" />
          Add bracket
        </Button>
        <Button
          v-if="canStopAgeSplit"
          variant="ghost"
          size="sm"
          @click="stopAgeSplit">
          Stop splitting by age
        </Button>
      </div>
    </LabeledField>
    <LabeledField label="Gender">
      <div>
        <label class="flex items-center gap-2 mb-2">
          <Switch v-model="getMAudience.segment_by_gender" />
          <span class="text-sm">Segment by gender</span>
        </label>
        <div class="flex gap-6">
          <label class="flex items-center gap-2">
            <Checkbox
              :model-value="getMAudience.t_gender?.men || getMAudience.segment_by_gender"
              :disabled="isGenderSegmentationActive"
              @update:model-value="updateMAudienceTGender('men', $event)" />
            <span class="text-sm">Men</span>
          </label>
          <label class="flex items-center gap-2">
            <Checkbox
              :model-value="getMAudience.t_gender?.women || getMAudience.segment_by_gender"
              :disabled="isGenderSegmentationActive"
              @update:model-value="updateMAudienceTGender('women', $event)" />
            <span class="text-sm">Women</span>
          </label>
        </div>
      </div>
    </LabeledField>
  </div>
</template>
